import type {
  MatchCandidateLocator,
  MatchVerdictDecision,
  PossibleMatchDto,
  PossibleMatchPersonDto,
  ProspectBookRowDto,
  RecordMatchVerdictRequest,
} from '@signalvote/shared';

/**
 * A first answer to "Is this them?" for one candidate: its locator exactly as the book served it, and the
 * decision. An unanswered candidate has no verdict yet, so the first answer carries no token.
 */
export const firstVerdict = (candidate: PossibleMatchDto, decision: MatchVerdictDecision): RecordMatchVerdictRequest => ({
  givenName: candidate.givenName,
  lastCore: candidate.lastCore,
  suffix: candidate.suffix,
  streetFolded: candidate.streetFolded,
  zip5: candidate.zip5,
  decision,
  concurrencyVersion: null,
});

// Short tokens people write in capitals: titles, company forms, generational suffixes and street directions.
const KEPT_CAPITALS = [
  'CEO', 'CFO', 'COO', 'CTO', 'CIO', 'CMO', 'VP', 'SVP', 'EVP', 'CPA', 'MD', 'DDS', 'DMD', 'RN', 'PHD', 'JD',
  'LLC', 'LLP', 'PLLC', 'LP', 'PC', 'PA', 'USA', 'US',
  'II', 'III', 'IV',
  'NE', 'NW', 'SE', 'SW', 'PO',
];
// Two-letter state codes, less the ones that are also everyday words, name particles or street and company
// abbreviations (IN, OR, ME, HI, OK, OH, LA, DE; CO for company, CT for court, MT for mount), which read as words.
const KEPT_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'DC', 'FL', 'GA', 'ID', 'IL', 'IA', 'KS', 'KY', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
  'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV',
  'WI', 'WY',
];
const KEPT = new Set([...KEPT_CAPITALS, ...KEPT_STATE_CODES]);

/** Shouting or a whisper read as a name: each word capitalised, a possessive "'s" left lower. */
const capitalise = (text: string) =>
  text
    .toLowerCase()
    .replace(/(^|[\s'-])(\p{L})/gu, (_, lead: string, letter: string) => lead + letter.toUpperCase())
    .replace(/'(\p{L})(?!\p{L})/gu, (_, letter: string) => `'${letter.toLowerCase()}`);

/** Put each whole word of `text` found in `kept` back in capitals. */
const keepCapitals = (text: string, kept: Set<string>) =>
  text.replace(/(?<![\p{L}\p{N}])\p{L}+(?![\p{L}\p{N}'])/gu, (word) => (kept.has(word.toUpperCase()) ? word.toUpperCase() : word));

const GENERATIONAL = new Set(['II', 'III', 'IV']);

/**
 * The FEC's spelling reads as shouting ("ACME BANK") and the folded locator as a whisper ("smith"); both read
 * as names. For an address, employer or occupation: short all-caps tokens — CEO, LLC, III, a state code, NW —
 * stay in capitals.
 */
export const titleCase = (text: string) => keepCapitals(capitalise(text), KEPT);

/** A person's name the same way, where only a generational suffix stays in capitals — "Al" is a name, not Alabama. */
export const titleCaseName = (text: string) => keepCapitals(capitalise(text), GENERATIONAL);

/** The largest gift among a person's possible-match candidates — the money a wrong answer puts at stake. Zero with none. */
export const largestCandidateGift = (row: ProspectBookRowDto) => row.largestCandidateGift ?? 0;

/** The same for a person on Match review, read from their candidates. Zero with none. */
export const largestWaitingGift = (person: PossibleMatchPersonDto) => Math.max(0, ...person.possibleMatches.map((c) => c.largestGift));

/** One candidate's locator as a single key — the same record on every read, whatever else it carries. */
export const candidateKey = (c: MatchCandidateLocator) => [c.givenName, c.lastCore, c.suffix, c.streetFolded, c.zip5].join('|');
